/**
 * Fixed-timestep simulation loop with accumulator; render runs at display rate
 * and receives an interpolation alpha in [0,1) between the last two sim states.
 */
export class Loop {
  readonly stepDt: number;
  private accumulator = 0;
  private last = 0;
  private raf = 0;
  running = false;
  /** Multiplies wall time; 0 pauses simulation (render keeps going). */
  timeScale = 1;
  /** Total simulated seconds. */
  simTime = 0;
  frameCount = 0;
  fps = 0;
  private fpsAcc = 0;
  private fpsFrames = 0;
  maxSubsteps = 5;

  constructor(
    private readonly update: (dt: number) => void,
    private readonly render: (alpha: number, frameDt: number) => void,
    hz = 60,
  ) {
    this.stepDt = 1 / hz;
  }

  start(): void {
    if (this.running) return;
    this.running = true;
    this.last = performance.now();
    const tick = (now: number) => {
      if (!this.running) return;
      this.raf = requestAnimationFrame(tick);
      let frameDt = (now - this.last) / 1000;
      this.last = now;
      if (frameDt > 0.25) frameDt = 0.25; // tab was hidden; don't spiral
      this.fpsAcc += frameDt; this.fpsFrames++;
      if (this.fpsAcc >= 0.5) { this.fps = this.fpsFrames / this.fpsAcc; this.fpsAcc = 0; this.fpsFrames = 0; }
      this.accumulator += frameDt * this.timeScale;
      let steps = 0;
      while (this.accumulator >= this.stepDt && steps < this.maxSubsteps) {
        this.update(this.stepDt);
        this.simTime += this.stepDt;
        this.accumulator -= this.stepDt;
        steps++;
      }
      if (steps === this.maxSubsteps) this.accumulator = 0;
      const alpha = this.timeScale > 0 ? this.accumulator / this.stepDt : 1;
      this.render(alpha, frameDt);
      this.frameCount++;
    };
    this.raf = requestAnimationFrame(tick);
  }

  stop(): void {
    this.running = false;
    cancelAnimationFrame(this.raf);
  }

  /** Run N sim steps synchronously without rendering (tests / bots). */
  stepSync(n: number): void {
    for (let i = 0; i < n; i++) { this.update(this.stepDt); this.simTime += this.stepDt; }
  }
}
